import { useState } from "react";
import type { CanonStatus, Relationship } from "../domain/types";
import { getCharacter, relationshipsAt } from "../domain/engine";
import { Field, Modal, SourceFields, StatusBadge, nowIso, submitGuard } from "../components/ui";
import { useStore } from "../store";

const TYPES = ["famille", "amitié", "amour", "rivalité", "alliance", "mentorat", "méfiance", "conflit", "rupture"];

export function Relations() {
  const { bible, selection, updateBible } = useStore();
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  if (!bible) return null;
  const rels = relationshipsAt(bible, selection.momentId).filter((r) => {
    if (!filter) return true;
    return r.fromCharacterId === filter || r.toCharacterId === filter;
  });
  return (
    <div>
      <div className="row space">
        <h1>Relations</h1>
        <button className="btn primary" type="button" onClick={() => setAdding(true)} disabled={!selection.momentId}>
          Nouvelle phase
        </button>
      </div>
      <p className="tiny">Les relations sont des phases datées, pas un texte libre. Une phase familiale peut coexister avec un conflit.</p>
      <select value={filter} onChange={(e) => setFilter(e.target.value)}>
        <option value="">Tous les personnages</option>
        {bible.characters.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      <div className="grid grid-2" style={{ marginTop: 12 }}>
        {rels.map((r) => {
          const a = getCharacter(bible, r.fromCharacterId);
          const b = getCharacter(bible, r.toCharacterId);
          return (
            <article key={r.id} className="card">
              <div className="row space">
                <h3>{a?.name} → {b?.name}</h3>
                <StatusBadge status={r.status} />
              </div>
              <p><strong>{r.label}</strong> · {r.type} {r.reciprocal ? "· réciproque" : "· asymétrique"}</p>
              <p>{r.summary}</p>
              <p className="tiny">{r.validFromMomentId} → {r.validToMomentId ?? "ouvert"} · {r.branchId}</p>
            </article>
          );
        })}
      </div>
      <RelForm
        open={adding}
        characters={bible.characters}
        defaultMomentId={selection.momentId}
        defaultBranchId={bible.moments.find((m) => m.id === selection.momentId)?.branchId ?? "branch:main"}
        onClose={() => setAdding(false)}
        onCreate={(rel) => {
          updateBible((d) => ({ ...d, relationships: [...d.relationships, rel] }), `Relation ${rel.label}`);
          setAdding(false);
        }}
      />
    </div>
  );
}

function RelForm({
  open,
  characters,
  defaultMomentId,
  defaultBranchId,
  onClose,
  onCreate,
}: {
  open: boolean;
  characters: Array<{ id: string; name: string }>;
  defaultMomentId: string | null;
  defaultBranchId: string;
  onClose: () => void;
  onCreate: (r: Relationship) => void;
}) {
  const [fromCharacterId, setFrom] = useState(characters[0]?.id ?? "");
  const [toCharacterId, setTo] = useState(characters[1]?.id ?? "");
  const [type, setType] = useState("amitié");
  const [label, setLabel] = useState("");
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState<CanonStatus>("draft");
  const [sourceLabel, setSourceLabel] = useState("");
  return (
    <Modal open={open} title="Phase de relation" onClose={onClose}>
      <form
        className="grid"
        onSubmit={(e) => {
          if (!submitGuard(e, status, sourceLabel) || !defaultMomentId) return;
          onCreate({
            id: `rel-${fromCharacterId}-${toCharacterId}-${Date.now().toString(36)}`,
            status,
            sources: sourceLabel ? [{ label: sourceLabel }] : [],
            createdAt: nowIso(),
            updatedAt: nowIso(),
            fromCharacterId,
            toCharacterId,
            type,
            label,
            summary,
            reciprocal: false,
            validFromMomentId: defaultMomentId,
            branchId: defaultBranchId,
          });
        }}
      >
        <Field label="De">
          <select value={fromCharacterId} onChange={(e) => setFrom(e.target.value)}>
            {characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Vers">
          <select value={toCharacterId} onChange={(e) => setTo(e.target.value)}>
            {characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Type">
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Libellé"><input value={label} onChange={(e) => setLabel(e.target.value)} required /></Field>
        <Field label="Résumé"><textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} /></Field>
        <SourceFields status={status} sourceLabel={sourceLabel} setStatus={setStatus} setSourceLabel={setSourceLabel} />
        <button className="btn primary" type="submit">Ajouter</button>
      </form>
    </Modal>
  );
}
